/**
 * Wat Quality Cashless per evenement overhoudt.
 *
 * Twee delen, zoals in deel 4 hoofdstuk 3:
 *
 *  1. het vaste tarief per kraamset per dag — dat is de marge waar je op
 *     stuurt;
 *  2. de transactiekosten, die je doorbelast in dezelfde vorm als je ze zelf
 *     betaalt. Wat daar tussen zit is klein en mag ook nul zijn.
 *
 * Het inkooptarief van de betaalpartij staat per evenement in het systeem. Dat
 * is de knop die je omzet als je betaalpartij zijn tarief wijzigt: een
 * instelling, geen verbouwing.
 *
 * BTW telt niet mee in de marge. Over de kraamsets rekenen we met het
 * nettobedrag; betaaldiensten zijn vrijgesteld van BTW, dus daar staat inkoop
 * tegenover doorbelasting zonder tussenstap.
 */

import {
  percentageVan,
  sommeer,
  tariefMaalAantal,
  type Basispunten,
  type Cent,
  type TiendeCent,
} from './geld.js';

/** Wat wij zelf aan de betaalpartij betalen. Zelfde vorm als het prijsmodel. */
export interface Inkoopmodel {
  /** In tienden van een cent: € 0,066 per pintransactie wordt 66. */
  perBonTiendeCent: TiendeCent;
  overOmzetBasispunten: Basispunten;
}

export const LEEG_INKOOPMODEL: Inkoopmodel = {
  perBonTiendeCent: 0,
  overOmzetBasispunten: 0,
};

export interface Transactievolume {
  aantalBonnen: number;
  /** Omzet exclusief statiegeld; daarover rekent niemand iets. */
  omzetCent: Cent;
}

export function berekenInkoopkosten(model: Inkoopmodel, volume: Transactievolume): Cent {
  return (
    tariefMaalAantal(model.perBonTiendeCent, volume.aantalBonnen) +
    percentageVan(Math.max(0, volume.omzetCent), model.overOmzetBasispunten)
  );
}

export interface MargeInvoer {
  /** Wat we bij de kramen in rekening hebben gebracht. */
  doorbelasteTransactiekostenCent: Cent;
  /** Wat we zelf aan de betaalpartij kwijt zijn. */
  inkoopTransactiekostenCent: Cent;
  /** De kraamsets, exclusief BTW. */
  kraamsetkostenNettoCent: Cent;
  /** Eigen kosten van dit evenement: reizen, aanwezigheid, verbruik. */
  eigenKostenCent?: Cent;
  /** Ingehouden vervangingswaarde van niet ingeleverde apparatuur. */
  inhoudingenCent?: Cent;
}

export interface Marge {
  opbrengstCent: Cent;
  kostenCent: Cent;
  margeCent: Cent;
  /** Wat er op de transactiekosten wordt verdiend of verloren. */
  transactiemargeCent: Cent;
}

export function berekenMarge(invoer: MargeInvoer): Marge {
  const eigenKostenCent = invoer.eigenKostenCent ?? 0;
  const inhoudingenCent = invoer.inhoudingenCent ?? 0;

  const opbrengstCent = sommeer([
    invoer.kraamsetkostenNettoCent,
    invoer.doorbelasteTransactiekostenCent,
    inhoudingenCent,
  ]);
  const kostenCent = sommeer([invoer.inkoopTransactiekostenCent, eigenKostenCent]);

  return {
    opbrengstCent,
    kostenCent,
    margeCent: opbrengstCent - kostenCent,
    transactiemargeCent: invoer.doorbelasteTransactiekostenCent - invoer.inkoopTransactiekostenCent,
  };
}
